import React, { useEffect, useState } from 'react'
import {
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Box,
  CircularProgress,
  FormControlLabel,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
} from '@mui/material'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { httpClient, apiUrl } from './admin/dataProvider'
import authProvider from './admin/authProvider'

const Verify2FAPage: React.FC = () => {
  const { t } = useTranslation()
  const [verifying, setVerifying] = useState(false)
  const [loggingOut, setLoggingOut] = useState(false)
  const [verificationCode, setVerificationCode] = useState('')
  const [rememberDevice, setRememberDevice] = useState(false)
  const [error, setError] = useState('')
  const [isSuperuser, setIsSuperuser] = useState<boolean>(false)
  const [showResetModal, setShowResetModal] = useState<boolean>(false)

  const navigate = useNavigate()

  useEffect(() => {
    // Fetch minimal identity to know if current user is a superuser without calling /users/me
    httpClient(`${apiUrl}/2fa/whoami`, { method: 'GET' })
      .then(({ json }: any) => {
        setIsSuperuser(!!json?.is_superuser)
      })
      .catch(() => {
        setIsSuperuser(false)
      })
  }, [])

  const handleVerify = async () => {
    if (!verificationCode) {
      setError(t('twofa.error_enter_code'))
      return
    }

    try {
      setVerifying(true)
      const { json } = await httpClient(`${apiUrl}/2fa/verify-login`, {
        method: 'POST',
        body: JSON.stringify({
          code: verificationCode,
          remember_device: rememberDevice,
        }),
      })

      if (json.success) {
        // Redirect to admin dashboard
        navigate('/admin')
      } else {
        setError(json.message || t('twofa.error_invalid_code'))
        setVerifying(false)
      }
      // eslint-disable-next-line @typescript-eslint/no-shadow
    } catch (error) {
      console.error('Error verifying 2FA code:', error)
      setError(t('twofa.error_verify_failed'))
      setVerifying(false)
    }
  }

  return (
    <Box
      display="flex"
      flexDirection="column"
      justifyContent="center"
      alignItems="center"
      height="100vh"
      sx={{
        backgroundColor: '#f5f5f5',
        backgroundImage: 'url(/logo192.png)',
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'center 40px',
        backgroundSize: '100px',
      }}
    >
      <Box mb={4} mt={-10}>
        <Typography variant="h4" component="h1" align="center" gutterBottom>
          Procurato - Spendtool
        </Typography>
      </Box>

      <Card sx={{ maxWidth: 600, width: '100%', p: 2, boxShadow: 3 }}>
        <CardContent>
          <Typography variant="h5" component="h2" gutterBottom>
            {t('twofa.verify_title')}
          </Typography>

          <Typography variant="body1" paragraph>
            {t('twofa.verify_intro')}
          </Typography>

          <TextField
            label={t('twofa.verification_code_label')}
            variant="outlined"
            fullWidth
            value={verificationCode}
            onChange={(e) => setVerificationCode(e.target.value)}
            error={!!error}
            helperText={error}
            placeholder={t('twofa.verification_code_placeholder')}
            sx={{ mb: 2 }}
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={rememberDevice}
                onChange={(e) => setRememberDevice(e.target.checked)}
              />
            }
            label={t('twofa.remember_device')}
            sx={{ mb: 2 }}
          />

          <Button
            variant="contained"
            color="primary"
            fullWidth
            onClick={handleVerify}
            disabled={verifying || loggingOut}
          >
            {verifying ? (
              <CircularProgress size={24} />
            ) : (
              t('twofa.verify_button')
            )}
          </Button>

          {isSuperuser && (
            <Box mt={2}>
              <Button
                variant="outlined"
                color="secondary"
                fullWidth
                onClick={async () => {
                  try {
                    await httpClient(`${apiUrl}/2fa/send-reset-email`, {
                      method: 'POST',
                    })
                  } catch (e) {
                    // ignore any error; we still show the modal and log out
                  } finally {
                    setShowResetModal(true)
                    setTimeout(async () => {
                      try {
                        await authProvider.logout({})
                      } catch (e) {
                        // ignore
                      } finally {
                        window.location.replace('/admin/login')
                      }
                    }, 2000)
                  }
                }}
                disabled={verifying || loggingOut}
              >
                Reset 2FA
              </Button>
            </Box>
          )}

          <Box mt={2}>
            <Button
              variant="text"
              color="secondary"
              fullWidth
              onClick={async () => {
                if (loggingOut) return
                setLoggingOut(true)
                try {
                  await authProvider.logout({})
                } catch (e) {
                  // ignore logout errors; proceed to login page
                } finally {
                  window.location.replace('/admin/login')
                }
              }}
              disabled={loggingOut || verifying}
            >
              {loggingOut ? <CircularProgress size={24} /> : t('twofa.logout')}
            </Button>
          </Box>
        </CardContent>
      </Card>

      <Dialog open={!!showResetModal} onClose={() => {}}>
        <DialogTitle>2FA Reset Email Sent</DialogTitle>
        <DialogContent>
          You have been sent a 2FA Reset Email. You will now be logged out.
        </DialogContent>
      </Dialog>
    </Box>
  )
}

export default Verify2FAPage
